import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import DocumentSignModalContext from './DocumentSignModalContext';
import { SignableDocument, SignableDocumentRecipient, SignablePrescripteur } from './types';

const DocumentSignModalProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const [dossierId, setDossierId] = useState<number | undefined>();
    const [prescripteur, setPrescripteur] = useState<SignablePrescripteur | undefined>();
    const [documents, setDocuments] = useState<Array<SignableDocument>>([]);
    const [recipients, setRecipients] = useState<Array<SignableDocumentRecipient>>([]);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [allowAddDocument, setAllowAddDocument] = useState<boolean>(false);

    const addDocument = useCallback(
        (document: SignableDocument) => {
            setDocuments([...documents, document]);
        },
        [setDocuments, documents],
    );

    const replaceDocuments = useCallback(
        (newDocuments: Array<SignableDocument>) => {
            setDocuments(newDocuments);
        },
        [setDocuments],
    );

    const clear = useCallback(() => {
        setDossierId(undefined);
        setPrescripteur(undefined);
        setDocuments([]);
        setRecipients([]);
    }, [setDocuments, setRecipients]);

    const addRecipient = useCallback(
        (recipient: SignableDocumentRecipient) => {
            setRecipients([...recipients, recipient]);
        },
        [setRecipients, recipients],
    );

    const removeRecipient = useCallback(
        (recipient: SignableDocumentRecipient) => {
            setRecipients(
                recipients.filter(
                    (r) =>
                        recipient.firstName !== r.firstName ||
                        recipient.lastName !== r.lastName ||
                        recipient.email !== r.email ||
                        recipient.mobile !== r.mobile,
                ),
            );
        },
        [setRecipients, recipients],
    );

    useEffect(() => {
        if (!modalOpen) {
            clear();
        }
    }, [modalOpen, clear]);

    const contextValue = useMemo(
        () => ({
            dossierId,
            prescripteur,
            documents,
            recipients,
            modalOpen,
            allowAddDocument,
            setDossierId,
            setPrescripteur,
            addDocument,
            replaceDocuments,
            addRecipient,
            removeRecipient,
            clear,
            setModalOpen,
            setAllowAddDocument,
        }),
        [
            dossierId,
            prescripteur,
            documents,
            recipients,
            modalOpen,
            allowAddDocument,
            setDossierId,
            setPrescripteur,
            addDocument,
            replaceDocuments,
            addRecipient,
            removeRecipient,
            clear,
            setModalOpen,
            setAllowAddDocument,
        ],
    );

    return (
        <DocumentSignModalContext.Provider value={contextValue}>
            {children}
        </DocumentSignModalContext.Provider>
    );
};

export default DocumentSignModalProvider;
